import { useEffect, useMemo, useState } from 'react';
import { Marker, Polygon } from 'react-leaflet';
import L from 'leaflet';
import { ArrowUp, Thermometer, Droplets, Wind, Flame } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { MapCanvas } from '@/components/maps/MapCanvas';
import { LongPressPin } from '@/components/maps/LongPressPin';
import { Button } from '@/components/ui/button';
import { WindParticleLayer } from '@/components/maps/wind/WindParticleLayer';
import { FdiDangerRing } from '@/components/maps/FdiDangerRing';
import { useWindField, stationsForTimestamp } from '@/hooks/useWindField';
import { useTenant } from '@/contexts/TenantContext';
import { useAuth } from '@/contexts/AuthContext';
import { listWildfiresForMap } from '@/api/wildfires';
import type { MapSettingsOptions } from '@/hooks/useMapSettings';
import {
  wildfireMarkerPosition,
  wildfirePolygonPathOptions,
  wildfirePolygonPositions,
  type WildfireMapShape,
} from '@/utils/wildfireMapStyle';
import { cn } from '@/lib/utils';

import 'leaflet/dist/leaflet.css';

/**
 * Fire pins start hidden here and the choice stays on this page — the
 * forecast is about conditions at one spot, and a national pin cloud
 * buries it. Declared module-level so the identity is stable.
 */
const FORECAST_MAP_SETTINGS: MapSettingsOptions = {
  overrides: { showPins: false },
  local: ['showPins'],
};

/** Wildfire markers, matching the wildfires map so the colours mean the
 *  same thing on both. Red = burning now, blue = resolved. */
const fireActiveIcon = L.icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const markerIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

interface HourlyEntry {
  timestamp?: string;
  temperature?: number | null;
  humidity?: number | null;
  wind_speed?: number | null;
  wind_gusts?: number | null;
  wind_direction?: number | null;
  fire_danger_index?: number | null;
}

interface ForecastMapProps {
  latitude: number;
  longitude: number;
  /** Re-point the whole forecast at a spot the user picked on the map. */
  onPickLocation?: (lat: number, lng: number) => void;
  hourly: HourlyEntry[];
  /** Initial hour 0–23. Defaults to the current hour clamped into the array. */
  initialHour?: number;
}

// ── Per-metric color thresholds ───────────────────────────────────────────

function fdiColorClass(value: number | null | undefined): string {
  if (value == null) return 'bg-muted text-muted-foreground';
  if (value < 21) return 'bg-fdi-safe text-white';
  if (value < 46) return 'bg-fdi-moderate text-white';
  if (value < 61) return 'bg-fdi-dangerous text-black';
  if (value < 76) return 'bg-fdi-very-dangerous text-white';
  return 'bg-fdi-extreme text-white';
}

function tempColorClass(v: number | null | undefined): string {
  if (v == null) return 'bg-muted text-muted-foreground';
  if (v < 10) return 'bg-blue-500 text-white';
  if (v < 25) return 'bg-emerald-500 text-white';
  if (v < 32) return 'bg-amber-500 text-black';
  return 'bg-red-500 text-white';
}

function humidityColorClass(v: number | null | undefined): string {
  if (v == null) return 'bg-muted text-muted-foreground';
  if (v < 25) return 'bg-amber-500 text-black';
  if (v < 60) return 'bg-emerald-500 text-white';
  return 'bg-blue-500 text-white';
}

function windColorClass(v: number | null | undefined): string {
  if (v == null) return 'bg-muted text-muted-foreground';
  if (v < 10) return 'bg-emerald-500 text-white';
  if (v < 25) return 'bg-amber-500 text-black';
  return 'bg-red-500 text-white';
}

function hourLabel(idx: number, hourly: HourlyEntry[]): string {
  const entry = hourly[idx];
  if (entry?.timestamp) {
    const d = new Date(entry.timestamp);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }
  return `${String(idx).padStart(2, '0')}:00`;
}

// ── Always-on metric badge ────────────────────────────────────────────────

interface MetricBadgeProps {
  icon: typeof Flame;
  label: string;
  value: string;
  colorClass: string;
  trailing?: React.ReactNode;
}

function MetricBadge({ icon: Icon, label, value, colorClass, trailing }: MetricBadgeProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium shadow-md',
        colorClass,
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      <span className="opacity-90">{label}</span>
      <span className="font-semibold">{value}</span>
      {trailing}
    </div>
  );
}

/**
 * Forecast map. Embedded inside ForecastContent. Shows the marker at the
 * forecast location, with all four metrics (FDI / Temp / Wind / Humidity)
 * pinned to the top-left as colored badges that scrub through the day's
 * hours via the slider. Reuses the global MapSettings primitive for tile
 * layer + overlay toggles (boundaries, parcels, wind, pins, polygons).
 *
 * The "Maximize" button takes the map fullscreen for on-scene viewing on
 * a phone; the hour slider stays visible in maximized mode.
 */
export function ForecastMap({ latitude, longitude, hourly, initialHour, onPickLocation }: ForecastMapProps) {
  const { tenantId } = useTenant();
  const { isAuthenticated } = useAuth();

  // Burn areas for context: where has this FPA actually burned? Signed-in
  // only, per the request. Drafts are dropped — someone else's unsubmitted
  // report has no business on a field-reference view.
  const [wildfires, setWildfires] = useState<WildfireMapShape[]>([]);
  useEffect(() => {
    if (!isAuthenticated) {
      setWildfires([]);
      return undefined;
    }
    let cancelled = false;
    const params: Record<string, unknown> = {};
    if (tenantId) params.tenant_id = tenantId;
    listWildfiresForMap(params)
      .then((data) => {
        if (cancelled) return;
        const rows = (data?.wildfires ?? []) as WildfireMapShape[];
        setWildfires(rows.filter((w) => w.review_status !== 'draft'));
      })
      .catch(() => {
        // Non-fatal: the forecast itself doesn't depend on this.
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, tenantId]);

  const now = new Date();
  const defaultHour = Math.max(0, Math.min(hourly.length - 1, initialHour ?? now.getHours()));
  const [hour, setHour] = useState<number>(defaultHour);

  const entry = hourly[hour] ?? hourly[0] ?? ({} as HourlyEntry);
  const center: [number, number] = useMemo(() => [latitude, longitude], [latitude, longitude]);

  const windDeg = entry.wind_direction ?? null;

  // Multi-station Open-Meteo wind field (cached for the session, shared with
  // any other map). Resolve the station samples for the scrubbed hour so the
  // particle field tracks the slider.
  const windField = useWindField(true);
  const stationsForHour = useMemo(
    () => stationsForTimestamp(windField.data, entry.timestamp),
    [windField.data, entry.timestamp],
  );

  if (hourly.length === 0) return null;

  return (
    <Card className="overflow-hidden">
      <div className="border-b border-border px-4 py-3">
        <div className="text-sm font-medium">On-scene view</div>
        <div className="text-xs text-muted-foreground">
          Scrub the slider to overlay forecast conditions at the location.
        </div>
      </div>

      <MapCanvas
        center={center}
        zoom={11}
        variant="inline"
        tenantId={tenantId ?? null}
        mapSearch
        mapSettings={FORECAST_MAP_SETTINGS}
        showWindToggle
        topLeftSlot={
          <>
            <MetricBadge
              icon={Flame}
              label="FDI"
              value={
                entry.fire_danger_index != null
                  ? String(Math.round(entry.fire_danger_index))
                  : '—'
              }
              colorClass={fdiColorClass(entry.fire_danger_index)}
            />
            <MetricBadge
              icon={Thermometer}
              label="Temp"
              value={entry.temperature != null ? `${entry.temperature.toFixed(1)}°C` : '—'}
              colorClass={tempColorClass(entry.temperature)}
            />
            <MetricBadge
              icon={Wind}
              label="Wind"
              value={
                entry.wind_speed != null ? `${Math.round(entry.wind_speed)} km/h` : '—'
              }
              colorClass={windColorClass(entry.wind_speed)}
              trailing={
                windDeg != null ? (
                  <ArrowUp
                    className="h-3 w-3"
                    style={{ transform: `rotate(${(windDeg + 180) % 360}deg)` }}
                  />
                ) : null
              }
            />
            <MetricBadge
              icon={Droplets}
              label="Humid"
              value={entry.humidity != null ? `${Math.round(entry.humidity)}%` : '—'}
              colorClass={humidityColorClass(entry.humidity)}
            />
            {entry.wind_gusts != null && (
              <div className="pointer-events-auto rounded-md bg-background/90 px-2 py-1 text-xs shadow-md">
                Gust: {Math.round(entry.wind_gusts)} km/h
              </div>
            )}
          </>
        }
        footerSlot={({ maximized }) => (
          <div
            className={cn(
              'space-y-2 border-t border-border px-4',
              maximized ? 'bg-background py-4' : 'py-3',
            )}
          >
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{hourLabel(hour, hourly)}</span>
              <span>
                {hour + 1} / {hourly.length}
              </span>
            </div>
            <Slider
              min={0}
              max={Math.max(0, hourly.length - 1)}
              step={1}
              value={[hour]}
              onValueChange={(v) => setHour(v[0] ?? 0)}
            />
          </div>
        )}
      >
        {({ settings }) => (
          <>
            <LongPressPin
              actions={(pin) =>
                onPickLocation ? (
                  <Button size="sm" onClick={() => onPickLocation(pin[0], pin[1])}>
                    Forecast here
                  </Button>
                ) : null
              }
            />
            {/* The forecast location itself, not a fire pin — deliberately
                not gated on showPins, which now governs wildfire markers. */}
            <Marker position={center} icon={markerIcon} />

            {wildfires.map((w, i) => {
              const ring = wildfirePolygonPositions(w);
              const marker = wildfireMarkerPosition(w);
              return (
                <div key={`wf-${i}`} className="contents">
                  {settings.showPolygons && ring && ring.length > 0 && (
                    <Polygon positions={ring} pathOptions={wildfirePolygonPathOptions(w)} />
                  )}
                  {settings.showPins && marker && (
                    <Marker
                      position={marker}
                      icon={w.is_active ? fireActiveIcon : markerIcon}
                    />
                  )}
                </div>
              );
            })}

            {/* FDI danger ring — pulsing cue around the location, sized and
                coloured by the scrubbed hour's fire danger index. Always on
                (independent of the pin/wind toggles). */}
            <FdiDangerRing position={center} fdi={entry.fire_danger_index} />

            {/* Animated multi-station wind particle field. Replaces the old
                static arrow; curls between stations when zoomed out. Gated by
                the gear's wind toggle (`showWindToggle` on MapCanvas). */}
            {settings.showWind && (
              <WindParticleLayer
                show
                stations={stationsForHour}
                basemap={settings.tileLayer}
              />
            )}
          </>
        )}
      </MapCanvas>
    </Card>
  );
}

export default ForecastMap;
